import {
    consoleLogger,
    CoreDI,
    IEventManagerWithAsync,
    injectable,
    IService,
    lazyInject,
    ScreenOrientation,
    System,
    SystemComponentType
} from '@gongxigames/framework/core';
import { EventTracker } from '@gongxigames/framework/core/utils/tracking/EventTracker';
import { ReelsEvents, SpinButtonEvents } from '../modules';
import { AutoplayOptions, DI, EnvironmentProperty, ILogic, LogicEvents, SlotSettings } from '..';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {ScreenManager} from '@gongxigames/framework/core/services/screenManager/ScreenManager';
import { fromCamelCaseToSnakeCase } from '@gongxigames/framework/core/utils/objectUtils';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

@injectable()
export class EventTrackerService implements IService {
    public static NAME = 'EventTrackerService';
    public type: SystemComponentType = 'service';

    @lazyInject(CoreDI.IEventManagerWithAsync)
    protected eventManager!: IEventManagerWithAsync;

    @lazyInject(CoreDI.Settings)
    protected settings!: SlotSettings;

    @lazyInject(DI.Logic)
    protected logic!: ILogic;

    @lazyInject(CoreDI.ScreenManager)
    private screenManager!: ScreenManager;

    @lazyInject(CoreDI.System)
    private system!: System;

    @lazyInject(CoreDI.EnvironmentProperty)
    private environmentProperty!: EnvironmentProperty;

    private eventTracker: EventTracker;

    private initialResourcesLoadStartTime = 0;

    private reelsSpinningStartTime = 0;

    private fpsSamples: number[] = [];

    private reelStopCounter = 0;

    private spinResponseTimes: number[] = [];

    private spinResponseCounter = 0;

    constructor() {
        const gameId = this.settings.get('gameName');
        const {gameName, gameVersion, framework} = this.environmentProperty.buildProperty.versions;
        const {skinName} = this.environmentProperty.buildProperty.gameProperty;

        this.eventTracker = new EventTracker({
            framework_version: framework,
            game_family: gameName,
            game_id: gameId,
            game_name: skinName,
            game_version: gameVersion,
        });

        this.eventManager.on('General.ResourcesLoading.START_LOAD_INITIAL_RESOURCES', this.onStartLoadInitialResources.bind(this), this);
        this.eventManager.on('General.Game.INITIALIZED', this.onGameInitialized.bind(this), this);
        this.eventManager.on('General.Game.START_GAME', this.onStartGame.bind(this), this);
        this.eventManager.on('General.Game.STARTED', this.onGameStarted.bind(this), this);
        this.eventManager.on(SpinButtonEvents.SPIN_BUTTON_PRESSED, this.onSpinButtonPressed.bind(this), this);
        this.eventManager.on(ReelsEvents.REELS_SPINNING_STARTED, this.onReelsSpinningStarted.bind(this), this);
        this.eventManager.on(ReelsEvents.ALL_REELS_STOPPED, this.onAllReelsStopped.bind(this), this);
        this.eventManager.on('Autoplay.START', this.onAutoplayStart.bind(this), this);
        this.eventManager.on('Autoplay.FORCE_STOP', this.onAutoplayForceStop.bind(this), this);
        this.eventManager.on('Autoplay.STOPPED', this.onAutoplayStop.bind(this), this);
        this.eventManager.on('General.BuyFeature.OPEN', this.onBuyFeatureOpen.bind(this), this);
        strictUiEmitter.on('UI.buyFeature.open', this.onBuyFeatureOpen.bind(this));
        this.eventManager.on('General.BuyFeature.BUY', this.onBuyFeatureBuy.bind(this), this);
        strictUiEmitter.on('UI.buyFeature.buy', this.onBuyFeatureBuy.bind(this));
        this.eventManager.on('General.BuyFeature.DECLINE', this.onBuyFeatureDecline.bind(this), this);
        strictUiEmitter.on('UI.buyFeature.decline', this.onBuyFeatureDecline.bind(this));
        this.eventManager.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponse.bind(this), this);

        this.trackGameLaunch();
    }

    public destroy(): void {
        consoleLogger.removeAllReceivers();
        consoleLogger.clear();
    }

    private trackGameLaunch(): void {
        const soundsMuted = this.settings.get('soundsMuted');
        const {buildType, math, integration} = window.versions;

        this.eventTracker.tag('game_launch', {
            build_type: buildType,
            math,
            integration,
            sounds_muted: soundsMuted
        });
    }

    private onStartLoadInitialResources(): void {
        this.initialResourcesLoadStartTime = performance.now();
    }

    private onGameInitialized(): void {
        const loadTime = performance.now() - this.initialResourcesLoadStartTime;

        this.eventTracker.tag('game_load', { load_time_ms: Math.round(loadTime) });
    }

    private onStartGame(): void {
        this.eventTracker.tag('start_button_pressed');
    }

    private onGameStarted(): void {
        this.eventTracker.tag('game_start',{
            orientation: this.screenManager.orientation
        });

        this.eventManager.on('General.Screen.ORIENTATION_CHANGED', this.onOrientationChanged.bind(this), this);

        strictUiEmitter.on('UI.sounds.enable', this.onSoundEnabled.bind(this));
        strictUiEmitter.on('UI.sounds.disable', this.onSoundDisabled.bind(this));
    }

    private onSpinButtonPressed(): void {
        this.eventTracker.tag('spin_button_pressed');
    }

    private onReelsSpinningStarted(): void {
        let spinType = 'regular';
        // this is awful but we need to check if the spin is turbo or not
        const isTurboSpin = this.settings.get('quickSpin') && this.logic.nextAction !== Action.FREESPIN && this.logic.nextAction !== Action.RESPIN;
        const autoplayMode = this.settings.get('autoplayMode');

        if (isTurboSpin) {
            spinType = 'turbo';
        }

        if (autoplayMode) {
            spinType = isTurboSpin ? 'auto-turbo' : 'auto';
        }
        this.eventTracker.tag('spin', { spin_type: spinType });
        this.reelsSpinningStartTime = performance.now();

        // Start collecting FPS samples using system.ticker
        this.fpsSamples = [];
        this.system.ticker.add(this.collectFpsSample, this);
    }

    private onAllReelsStopped(): void {
        this.system.ticker.remove(this.collectFpsSample, this);

        this.reelStopCounter++;

        if (this.reelStopCounter % 10 === 0) {
            const averageFps = this.fpsSamples.length
                ? this.fpsSamples.reduce((sum, fps) => sum + fps, 0) / this.fpsSamples.length
                : 0;

            this.eventTracker.tag('average_fps', { value: Math.round(averageFps) });
        }
    }

    private collectFpsSample(): void {
        this.fpsSamples.push(Math.round(this.system.ticker.FPS));
    }

    protected onAutoplayStart(options: AutoplayOptions): void {
        const snakeCaseOptions = fromCamelCaseToSnakeCase(options);

        // send the options to the event tracker
        this.eventTracker.tag('autoplay_start', snakeCaseOptions);
    }

    protected onAutoplayForceStop(): void {
        this.eventTracker.tag('autoplay_force_stop');
    }

    protected onAutoplayStop(): void {
        this.eventTracker.tag('autoplay_stop');
    }

    protected onBuyFeatureOpen(): void {
        this.eventTracker.tag('buy_feature_open');
    }

    protected onBuyFeatureBuy(): void {
        this.eventTracker.tag('buy_feature_buy');
    }

    protected onBuyFeatureDecline(): void {
        this.eventTracker.tag('buy_feature_decline');
    }

    protected onOrientationChanged(orientation: ScreenOrientation): void {
        this.eventTracker.tag('orientation_changed', { orientation });
    }

    protected onSoundEnabled(): void {
        this.eventTracker.tag('sounds_muted', { value: false });
    }

    protected onSoundDisabled(): void {
        this.eventTracker.tag('sounds_muted', { value: true });
    }

    protected onSpinResponse(): void {
        const spinTime = performance.now() - this.reelsSpinningStartTime;

        this.spinResponseTimes.push(spinTime);
        this.spinResponseCounter++;

        if (this.spinResponseCounter % 10 === 0) {
            const averageSpinTime = this.spinResponseTimes.length
                ? this.spinResponseTimes.reduce((sum, time) => sum + time, 0) / this.spinResponseTimes.length
                : 0;

            this.eventTracker.tag('average_spin_response_time', { value: Math.round(averageSpinTime) });
            this.spinResponseTimes = []; // Reset the array after tracking
        }
    }
}
